// src/pages/bloomterminal/utils/dashboardLayoutHooks.js
import { useCallback, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useUser } from '../../context/UserContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { verticalCompactor } from 'react-grid-layout';
import { DEFAULT_LAYOUT, CHART_REGISTRY } from '../config/chartRegistry';

/** Must match WidgetDashboard COLS */
const BREAKPOINT_COLS = { lg: 48, md: 40, sm: 24, xs: 16, xxs: 8 };

/**
 * Pack widgets vertically with no empty rows (custom view auto-arrange).
 */
export const compactDashboardLayouts = (layouts) => {
  if (!layouts) return layouts;
  const result = {};
  Object.keys(layouts).forEach((bp) => {
    const cols = BREAKPOINT_COLS[bp] ?? 48;
    const items = (layouts[bp] || []).map((item) => ({ ...item }));
    result[bp] = verticalCompactor.compact(items, cols);
  });
  return result;
};

/**
 * Debounce utility function
 */
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Sanitize layout data to remove undefined values (Firestore doesn't accept undefined)
 */
const sanitizeLayoutForFirestore = (layout) => {
  if (!layout) return layout;

  const sanitize = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(sanitize);
    }
    if (obj && typeof obj === 'object') {
      const cleaned = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = sanitize(value);
        }
      }
      return cleaned;
    }
    return obj;
  };

  return sanitize(layout);
};

/**
 * Generate layout for a new widget based on existing layout
 */
const generateLayoutForNewWidget = (widgetId, existingLayouts, breakpoint) => {
  const chartConfig = CHART_REGISTRY[widgetId];
  if (!chartConfig) return null;

  const { defaultSize } = chartConfig;
  const existingLayout = existingLayouts[breakpoint] || [];

  // Find the next available position (below existing widgets)
  let maxY = 0;
  existingLayout.forEach((item) => {
    const itemBottom = item.y + item.h;
    if (itemBottom > maxY) maxY = itemBottom;
  });

  return {
    i: widgetId,
    x: 0,
    y: maxY,
    w: defaultSize.w,
    h: defaultSize.h,
    minW: chartConfig.minSize?.w || 1,
    minH: chartConfig.minSize?.h || 1,
  };
};

/**
 * Add a new widget to all breakpoint layouts
 */
const addWidgetToLayouts = (widgetId, currentLayouts) => {
  const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'];
  const newLayouts = { ...currentLayouts };

  breakpoints.forEach((bp) => {
    const newWidgetLayout = generateLayoutForNewWidget(widgetId, currentLayouts, bp);
    if (newWidgetLayout) {
      newLayouts[bp] = [...(currentLayouts[bp] || []), newWidgetLayout];
    }
  });

  return newLayouts;
};

/**
 * Remove a widget from all breakpoint layouts
 */
const removeWidgetFromLayouts = (widgetId, currentLayouts) => {
  const newLayouts = {};
  Object.keys(currentLayouts).forEach((bp) => {
    newLayouts[bp] = currentLayouts[bp].filter((item) => item.i !== widgetId);
  });
  return newLayouts;
};

/**
 * Custom hook for managing dashboard layout with Firestore persistence
 */
export const useDashboardLayout = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();
  const debouncedSaveRef = useRef(null);

  // Firestore document reference
  const getDocRef = useCallback(() => {
    if (!email) return null;
    return doc(db, 'userPreferences', email, 'analytics', 'dashboardLayout');
  }, [email]);

  // Fetch layout from Firestore
  const {
    data: savedLayout,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['dashboardLayout', email],
    queryFn: async () => {
      const docRef = getDocRef();
      if (!docRef) return DEFAULT_LAYOUT;

      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Validate the layout structure
          if (data.activeWidgets && data.layouts) {
            return {
              ...data,
              layouts: compactDashboardLayouts(data.layouts),
            };
          }
        }
        return DEFAULT_LAYOUT;
      } catch (err) {
        console.error('Error fetching dashboard layout:', err);
        return DEFAULT_LAYOUT;
      }
    },
    enabled: !!email,
    staleTime: Infinity, // Layout rarely changes from external sources
    gcTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
  });

  // Save layout mutation
  const saveMutation = useMutation({
    mutationFn: async (newLayout) => {
      const docRef = getDocRef();
      if (!docRef) {
        console.warn('Cannot save layout: no user email');
        return;
      }

      // Sanitize layout to remove undefined values (Firestore doesn't accept them)
      const sanitizedLayout = sanitizeLayoutForFirestore(newLayout);

      await setDoc(
        docRef,
        {
          ...sanitizedLayout,
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );
    },
    onSuccess: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['dashboardLayout', email] });
    },
    onError: (err) => {
      console.error('Error saving dashboard layout:', err);
    },
  });

  // Initialize debounced save function
  if (!debouncedSaveRef.current) {
    debouncedSaveRef.current = debounce((layout) => {
      saveMutation.mutate(layout);
    }, 1000);
  }

  // Optimistically update local cache
  const updateLayoutOptimistically = useCallback(
    (newLayout) => {
      queryClient.setQueryData(['dashboardLayout', email], newLayout);
      debouncedSaveRef.current(newLayout);
    },
    [email, queryClient]
  );

  // Add widget handler
  const addWidget = useCallback(
    (widgetId) => {
      // Get the latest layout from cache to avoid stale data
      const currentLayout = queryClient.getQueryData(['dashboardLayout', email]) || savedLayout || DEFAULT_LAYOUT;

      // Don't add if already exists
      if (currentLayout.activeWidgets.includes(widgetId)) {
        return;
      }

      const newActiveWidgets = [...currentLayout.activeWidgets, widgetId];
      const newLayouts = addWidgetToLayouts(widgetId, currentLayout.layouts);

      const newLayout = {
        ...currentLayout,
        activeWidgets: newActiveWidgets,
        layouts: compactDashboardLayouts(newLayouts),
      };

      updateLayoutOptimistically(newLayout);
    },
    [email, queryClient, savedLayout, updateLayoutOptimistically]
  );

  // Remove widget handler
  const removeWidget = useCallback(
    (widgetId) => {
      // Get the latest layout from cache to avoid stale data
      const currentLayout = queryClient.getQueryData(['dashboardLayout', email]) || savedLayout || DEFAULT_LAYOUT;

      const newActiveWidgets = currentLayout.activeWidgets.filter((id) => id !== widgetId);
      const newLayouts = removeWidgetFromLayouts(widgetId, currentLayout.layouts);

      const newLayout = {
        ...currentLayout,
        activeWidgets: newActiveWidgets,
        layouts: compactDashboardLayouts(newLayouts),
      };

      updateLayoutOptimistically(newLayout);
    },
    [email, queryClient, savedLayout, updateLayoutOptimistically]
  );

  // Handle layout change from drag/resize
  const handleLayoutChange = useCallback(
    (currentLayout, allLayouts) => {
      const currentSavedLayout = savedLayout || DEFAULT_LAYOUT;

      const newLayout = {
        ...currentSavedLayout,
        layouts: compactDashboardLayouts(allLayouts),
      };

      updateLayoutOptimistically(newLayout);
    },
    [savedLayout, updateLayoutOptimistically]
  );

  // Reset to default layout
  const resetToDefault = useCallback(() => {
    updateLayoutOptimistically(DEFAULT_LAYOUT);
  }, [updateLayoutOptimistically]);

  /**
   * Apply full widget selection from Add Widget modal (batch add/remove, single save).
   * Builds layouts from selectedIds and persists once to Firestore.
   */
  const applyWidgetSelection = useCallback(
    (selectedIds) => {
      const currentLayout = queryClient.getQueryData(['dashboardLayout', email]) || savedLayout || DEFAULT_LAYOUT;
      const validIds = (selectedIds || []).filter((id) => CHART_REGISTRY[id]);
      const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'];
      const newLayouts = {};

      for (const bp of breakpoints) {
        const currentItems = currentLayout.layouts[bp] || [];
        const byId = Object.fromEntries(currentItems.map((item) => [item.i, item]));
        const ordered = [];
        for (const id of validIds) {
          if (byId[id]) {
            ordered.push(byId[id]);
          } else {
            const builtSoFar = { ...currentLayout.layouts, [bp]: ordered };
            const newItem = generateLayoutForNewWidget(id, builtSoFar, bp);
            if (newItem) ordered.push(newItem);
          }
        }
        newLayouts[bp] = ordered;
      }

      const newLayout = {
        ...currentLayout,
        activeWidgets: validIds,
        layouts: compactDashboardLayouts(newLayouts),
      };
      updateLayoutOptimistically(newLayout);
    },
    [email, queryClient, savedLayout, updateLayoutOptimistically]
  );

  return {
    layout: savedLayout || DEFAULT_LAYOUT,
    isLoading,
    isError,
    error,
    isSaving: saveMutation.isPending,
    addWidget,
    removeWidget,
    applyWidgetSelection,
    handleLayoutChange,
    resetToDefault,
  };
};

export default useDashboardLayout;
